<template>
  <div>
    <back-link programmatic :routes="backLinkRoutes" class="back-link">
      {{ $t('GO_BACK') }}
    </back-link>

    <div class="section" v-if="isPending && !installationUrl && !isInstallationFinishedOnDevice">
      <h1 class="heading">{{ $t('APP_INSTALLATION_PAGE_HEADING') }}</h1>
      <v-message type="warning" class="section__message">{{ $t('APP_INSTALLATION_PAGE_WARNING') }}</v-message>
      <v-loader class="section__loader" />
    </div>

    <div v-if="errorMessage" class="section">
      <v-message type="error">{{ errorMessage }}</v-message>
    </div>

    <div class="section" v-if="installationUrl || jitInProgress || isInstallationFinishedOnDevice">
      <v-message v-if="installationUrl && !isInstallationFinishedOnDevice" type="success" class="section__message">{{
        $t('APP_INSTALLATION_PAGE_SUCCESS')
      }}</v-message>
      <v-message v-if="isInstallationFinishedOnDevice" type="success" class="section__message">{{
        $t('APP_INSTALLATION_PAGE_SUCCESS_ON_DEVICE')
      }}</v-message>
      <v-button v-if="installationUrl && !isInstallationFinishedOnDevice" class="section__button" tag="a" size="lg"
        type="primary" :href="installationUrl">
        {{ $t('INSTALL') }}
      </v-button>

      <!-- JIT Support section -->
      <div v-if="askForJit == 1" class="jit-section">
        <!-- Show JIT button for devices that support JIT -->
        <div v-if="currentDevice && currentDevice.can_use_jit === 'yes'" class="jit-section__support">
          <p class="jit-section__notice">{{ $t('JIT_INSTALL_NOTICE') }}</p>

          <!-- Show success message if JIT was enabled successfully -->
          <p v-if="jitSucceeded" class="jit-section__success">{{ $t('JIT_ENABLED_SUCCESSFULLY') }}</p>

          <!-- Show JIT button if not yet successful -->
          <template v-else>
            <v-button v-if="!jitInProgress" class="jit-section__button" size="md" type="secondary"
              @click="confirmEnableJit">
              {{ $t('ENABLE_JIT_BUTTON') }}
            </v-button>
          </template>
        </div>

        <!-- Message for devices that don't support JIT yet -->
        <div v-else-if="currentDevice && currentDevice.can_use_jit === 'no'" class="jit-section__no-support">
          <html-with-links tag="p" class="jit-section__notice" :html="$t('JIT_DEVICE_NOT_CONFIGURED_WITH_LINK', {
            href: '/enable-jit'
          })"></html-with-links>

          <!-- Display validation error if available -->
          <html-with-links v-if="currentDevice.can_use_jit_validation_error" tag="p" class="jit-section__error"
            :html="currentDevice.can_use_jit_validation_error.translated"></html-with-links>
        </div>
      </div>
    </div>

    <commands-table class="commands" :commands="commands" />

    <!-- Connection modal -->
    <modal name="connection-dialog" :height="'auto'" :width="300" :adaptive="true" :click-to-close="false">
      <div class="connection-dialog">
        <div class="connection-dialog__content">
          <p>{{ connectionDialogContent }}</p>
        </div>
        <div class="connection-dialog__buttons">
          <button class="g-button" @click="handleCancelConnection">{{ $t('CANCEL') }}</button>
          <a :href="shortcutUrl" class="g-button g-button_primary">{{ $t('CONNECT') }}</a>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import BackLink from '@/components/BackLink.vue';
import CommandsTable from '@/components/CommandsTable.vue';
import VMessage from '@/components/VMessage.vue';
import VButton from '@/components/Button.vue';
import VLoader from '@/components/Loader';
import HtmlWithLinks from '@/components/HtmlWithLinks.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    BackLink,
    CommandsTable,
    VLoader,
    VMessage,
    VButton,
    HtmlWithLinks,
  },
  data() {
    return {
      isPending: true,
      commands: [],
      fetchTimeout: null,
      backLinkRoutes: ['app-details', 'my-apps', 'my-installation-history'],
      jitInProgress: false,
      shortcutUrl: 'shortcuts://run-shortcut?name=connect%20to%20appdb&input=text&text=https%3A%2F%2Fappdb.to%2Fclose.html',
      activeCommandUuid: null,
      showingConnectionPrompt: false,
      intentionallyNavigatingBack: false,
      connectionDialogContent: '',
      connectionCancelMessage: '',
    };
  },
  computed: {
    ...mapGetters(['currentDevice']),
    title() {
      return this.$t('APP_INSTALLATION_PAGE_TITLE');
    },
    commandUuid() {
      return this.$route.query.commandUuid;
    },
    askForJit() {
      return this.$route.query.askForJit;
    },
    historyUuid() {
      return this.$route.query.historyUuid;
    },
    errorMessage() {
      const failedCommand = this.commands.find(command => command.status.includes('failed'));
      if (!failedCommand) {
        return;
      }

      return failedCommand.params?.error || this.$t('SOMETHING_WENT_WRONG');
    },
    manifestUrl() {
      const manifestCommand = this.commands.find(command => command.status === 'ok' && command.params.manifest_uri);
      if (!manifestCommand) {
        return;
      }

      return manifestCommand.params?.manifest_uri;
    },
    isInstallationFinishedOnDevice() {
      const finishedCommand = this.commands.find(command => command.status === 'ok' && command.params.is_installation_finished);
      return !!finishedCommand;
    },
    jitSucceeded() {
      const jitSucceeded = this.commands.find(command => command.status === 'ok' && command.type === 'enable_jit');
      if (!jitSucceeded) {
        return false;
      }

      return true;
    },
    installationUrl() {
      if (!this.manifestUrl) {
        return;
      }

      const url = new URL('itms-services://');
      url.searchParams.set('action', 'download-manifest');
      url.searchParams.set('url', this.manifestUrl);
      return url.toString();
    },
  },
  methods: {
    async handleCancelConnection() {
      // First hide the dialog but don't reset the flag yet
      this.$modal.hide('connection-dialog');

      try {
        const confirmCancel = await this.$confirm(
          this.connectionCancelMessage,
          { okText: this.$t('YES'), cancelText: this.$t('NO'), hidePage: true },
        );

        if (confirmCancel) {
          // User confirmed cancellation - now we can set the flag to false
          this.showingConnectionPrompt = false;
          // Stop the JIT process
          this.jitInProgress = false;
          // Also ensure isPending is false to prevent beforeRouteLeave confirmation
          this.isPending = false;
          clearTimeout(this.fetchTimeout);

          // Cancel the command via API if we have an active command UUID
          if (this.activeCommandUuid) {
            try {
              await this.$api.call('cancel_command', { uuid: this.activeCommandUuid });
              console.log('Command cancelled successfully:', this.activeCommandUuid);
            } catch (error) {
              console.error('Error cancelling command:', error);
            }
          } else {
            console.log('No active command UUID to cancel on page leave');
          }

          // Remove the beforeunload event listener before navigation
          window.removeEventListener('beforeunload', this.handleBeforeUnload);

          // Set flag to indicate we're intentionally navigating back
          this.intentionallyNavigatingBack = true;

          // Navigate back
          this.$router.back();
        } else {
          // User clicked No, show the connection dialog again
          console.log('User decided to continue the JIT session, showing connection dialog again');
          this.$modal.show('connection-dialog');
        }
      } catch {
        // User clicked No (or closed dialog), show the connection dialog again
        console.log('User decided to continue the JIT session, showing connection dialog again');
        this.$modal.show('connection-dialog');
      }
    },
    async fetchCommands() {
      // Check early exit conditions
      if (!this.isPending && !this.jitInProgress) {
        console.log('Exiting fetchCommands early - isPending:', this.isPending, 'jitInProgress:', this.jitInProgress, 'activeCommandUuid:', this.activeCommandUuid);
        return;
      }

      try {
        // Make sure we're using the most current command UUID
        const sinceUuid = this.activeCommandUuid || this.$route.query.commandUuid;
        console.log('Fetching commands with UUID:', sinceUuid);

        if (!sinceUuid) {
          console.error('No command UUID available for fetching');
          // Only set isPending to false if we're not in JIT mode
          if (!this.jitInProgress) {
            this.isPending = false;
          }
          return;
        }

        try {
          // Wrap the API call in its own try/catch to handle API-specific errors
          this.commands = await this.$api.getDeviceStatus({ sinceUuid });

          console.log('Received commands:', this.commands.length, 'isPending:', this.isPending, 'installationUrl exists:', !!this.installationUrl);

          // Check if installation is finished on device
          const installFinishedOnDevice = this.commands.find(command => command.status === 'ok' && command.params.is_installation_finished);
          if (installFinishedOnDevice) {
            console.log('Installation finished on device, stopping polling');
            this.isPending = false;
            clearTimeout(this.fetchTimeout);
            return;
          }

          if (this.jitInProgress) {
            const jitCommand = this.commands.find(command => command.type === 'enable_jit');
            if (jitCommand) {
              console.log('Found JIT command with status:', jitCommand.status);

              // If status changed from waiting_for_connection, hide the dialog
              if (jitCommand.status !== 'waiting_for_connection' && this.showingConnectionPrompt) {
                this.$modal.hide('connection-dialog');
                this.showingConnectionPrompt = false;
                console.log('Connection dialog hidden due to status change to:', jitCommand.status);
              }

              // Check for waiting_for_connection status
              if (jitCommand.status === 'waiting_for_connection' && !this.showingConnectionPrompt) {
                this.showingConnectionPrompt = true; // Prevent multiple prompts

                // Set dialog content for JIT
                this.connectionDialogContent = this.$t('DEVICE_CONNECT_JIT_REQUIREMENT');
                this.connectionCancelMessage = this.$t('CONFIRM_CANCEL_JIT_SESSION');

                // Show the connection dialog with the link button
                this.$modal.show('connection-dialog');
              }

              if (jitCommand.status === 'ok' || jitCommand.status.match(/failed/)) {
                this.jitInProgress = false;
                // Hide connection dialog if it's open - redundant but kept for safety
                if (this.showingConnectionPrompt) {
                  this.$modal.hide('connection-dialog');
                  this.showingConnectionPrompt = false;
                }

                if (jitCommand.status === 'ok') {
                  this.$notify({
                    type: 'success',
                    text: this.$t('JIT_ENABLED_SUCCESSFULLY_NOTIFICATION'),
                  });

                  // Also ensure isPending is false to prevent beforeRouteLeave confirmation
                  this.isPending = false;

                  // Remove the beforeunload event listener before navigation
                  window.removeEventListener('beforeunload', this.handleBeforeUnload);

                  // Set flag to indicate we're intentionally navigating back
                  this.intentionallyNavigatingBack = true;

                  // Navigate back after successful JIT enablement
                  this.$router.back();
                } else {
                  this.$notify({
                    type: 'error',
                    text: this.$t('JIT_ENABLEMENT_FAILED'),
                  });
                }
              }
            }
          }

          // Handle install_app commands with waiting_for_connection status
          const installCommand = this.commands.find(command => command.type === 'install_app');
          if (installCommand) {
            console.log('Found install_app command with status:', installCommand.status);

            // If status changed from waiting_for_connection, hide the dialog
            if (installCommand.status !== 'waiting_for_connection' && this.showingConnectionPrompt) {
              this.$modal.hide('connection-dialog');
              this.showingConnectionPrompt = false;
              console.log('Connection dialog hidden due to status change to:', installCommand.status);
            }

            // Check for waiting_for_connection status
            if (installCommand.status === 'waiting_for_connection' && !this.showingConnectionPrompt) {
              this.showingConnectionPrompt = true; // Prevent multiple prompts

              // Set dialog content for install_app
              this.connectionDialogContent = this.$t('DEVICE_CONNECT_INSTALL_REQUIREMENT');
              this.connectionCancelMessage = this.$t('CONFIRM_CANCEL_INSTALL_SESSION');

              // Show the connection dialog with the link button
              this.$modal.show('connection-dialog');
            }

            if (installCommand.status === 'ok' || installCommand.status.match(/failed/)) {
              // Hide connection dialog if it's open
              if (this.showingConnectionPrompt) {
                this.$modal.hide('connection-dialog');
                this.showingConnectionPrompt = false;
              }
            }
          }
        } catch (apiError) {
          console.error('API error fetching commands:', apiError);

          // Handle specific API errors
          if (this.jitInProgress) {
            this.$notify({
              type: 'error',
              text: this.$t('ERROR_CHECKING_JIT_STATUS'),
            });
            // Don't navigate back but do stop the JIT progress
            this.jitInProgress = false;
          } else if (this.isPending) {
            this.$notify({
              type: 'error',
              text: this.$t('ERROR_FETCHING_INSTALLATION_STATUS'),
            });
            this.isPending = false;
            // Only navigate back if we're in the initial loading state
            this.$store.dispatch('router/back', { routes: this.backLinkRoutes });
          }

          // Don't schedule another fetch when there's an API error
          return;
        }

        // Continue the polling cycle
        clearTimeout(this.fetchTimeout); // Clear any existing timeout to prevent duplicates
        this.fetchTimeout = setTimeout(this.fetchCommands, 1000);
        console.log('Scheduling next fetch in 1 second');
      } catch (error) {
        // This is for catastrophic errors outside the API call
        console.error('Critical error in fetchCommands:', error);
        this.$notify({
          type: 'error',
          text: this.$t('UNEXPECTED_ERROR_OCCURRED'),
        });

        // Always cleanup state
        this.isPending = false;
        this.jitInProgress = false;
        clearTimeout(this.fetchTimeout);

        // Don't automatically navigate back on errors during JIT process
      }
    },

    // Dedicated method to start the polling cycle
    startPolling() {
      // Log what's happening and make sure we have a valid UUID
      console.log('Starting polling cycle with UUID:', this.activeCommandUuid);

      if (!this.activeCommandUuid) {
        console.error('Cannot start polling: no activeCommandUuid available');
        return;
      }

      // Clear any existing timeout to prevent duplicate calls
      clearTimeout(this.fetchTimeout);

      // Add a small delay before the first poll to give the server time to update status
      this.fetchTimeout = setTimeout(() => {
        this.fetchCommands();
      }, 1000);
    },

    validateQuery() {
      const routeCommandUuid = this.$route.query.commandUuid;
      console.log('validateQuery - route commandUuid:', routeCommandUuid);

      if (!routeCommandUuid) {
        this.isPending = false;
        this.$store.dispatch('router/back', { routes: this.backLinkRoutes });
      } else {
        this.activeCommandUuid = routeCommandUuid;
        console.log('activeCommandUuid set to:', this.activeCommandUuid);
      }
    },
    handleDataChange() {
      // Only set isPending to false if we're not in the middle of a JIT process
      // or if installation is finished on device
      if ((this.errorMessage || this.installationUrl || this.isInstallationFinishedOnDevice) && !this.jitInProgress) {
        this.isPending = false;
      }
    },
    handleBeforeUnload(e) {
      e.preventDefault();
    },
    async confirmEnableJit() {
      // Check if user can install apps
      if (!this.$store.getters.canInstallApps) {
        await this.$alert(this.$t('TO_ENABLE_JIT_VISIT_FROM_DEVICE', { device: this.config.nice_idevice_model }));
        return;
      }

      try {
        const confirmed = await this.$confirm(this.$t('CONFIRM_APP_INSTALLED'), {
          okText: this.$t('YES'),
          cancelText: this.$t('NO'),
          hidePage: true,
        });

        if (confirmed) {
          this.startEnableJitSession();
        }
      } catch {
        // User cancelled
      }
    },
    startEnableJitSession() {
      // Set jitInProgress flag before making the API call
      this.jitInProgress = true;

      console.log('Starting JIT session with history UUID:', this.historyUuid);

      // Use Promise chaining instead of async/await
      this.$api.call('start_enable_jit_session', {
        installation_uuid: this.historyUuid,
      })
        .then((response) => {
          console.log('JIT session API response:', response);

          if (response && response.bundle_id) {
            console.log('Received bundle_id:', response.bundle_id);

            // Show JIT enable modal after ensuring Vue is ready and navigate back after
            this.$nextTick(() => {
              this.$modal.show('jit-enable', {
                bundleId: response.bundle_id,
              });
            });

            // Navigate back after the modal is shown
            this.jitInProgress = false;
            this.intentionallyNavigatingBack = true;
            this.$router.back();
          } else {
            console.error('No bundle_id received in API response:', response);
            this.$notify({
              type: 'error',
              text: this.$t('FAILED_START_JIT_SESSION'),
            });
            this.jitInProgress = false;
          }
        })
        .catch((error) => {
          console.error('Error starting JIT session:', error);
          this.$notify({
            type: 'error',
            text: this.$t('FAILED_START_JIT_SESSION'),
          });
          this.jitInProgress = false;
        });
    },
  },
  watch: {
    errorMessage() {
      this.handleDataChange();
    },
    installationUrl() {
      this.handleDataChange();
    },
    isInstallationFinishedOnDevice() {
      this.handleDataChange();
    },
    isPending() {
      if (!this.isPending && !this.jitInProgress) {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        clearTimeout(this.fetchTimeout);
      }
    },
    jitInProgress(newVal) {
      // When JIT process starts, log state and ensure UI visibility
      console.log('jitInProgress changed to:', newVal, 'isPending:', this.isPending, 'installationUrl:', !!this.installationUrl);

      // Make sure we don't remove event listeners or timeouts while JIT is in progress
      if (newVal && !this.isPending) {
        window.addEventListener('beforeunload', this.handleBeforeUnload);
      }
    },
  },
  created() {
    console.log('Component created with commandUuid:', this.$route.query.commandUuid);
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    this.validateQuery();

    // Handle case when user arrives from external link with JIT parameters
    const { askForJit, enableJit } = this.$route.query;
    if (askForJit === '1' && enableJit === '1' && this.$route.query.commandUuid) {
      console.log('Detected external redirect for JIT enablement');
      // Set JIT in progress state
      this.jitInProgress = true;
      // Store history UUID for reference
      this.historyUuid = this.$route.query.historyUuid;

      // Let the fetchCommands cycle detect the waiting_for_connection status
      // and show the dialog at the appropriate time
      console.log('JIT is in progress, waiting for command status before showing dialog');
    }

    this.startPolling(); // Use the dedicated method to start polling
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    clearTimeout(this.fetchTimeout);
  },
  async beforeRouteLeave(to, from, next) {
    // If we're intentionally navigating back or neither pending nor in JIT process, proceed without confirmation
    if (this.intentionallyNavigatingBack || (!this.isPending && !this.jitInProgress)) {
      return next();
    }

    try {
      await this.$confirm(this.$t('APP_INSTALLATION_PAGE_LEAVE_CONFIRMATION'), { okText: this.$t('YES'), hidePage: true });

      // Cancel the command via API if we have an active command UUID
      if (this.activeCommandUuid) {
        try {
          await this.$api.call('cancel_command', { uuid: this.activeCommandUuid });
          console.log('Command cancelled successfully on page leave:', this.activeCommandUuid);
        } catch (error) {
          console.error('Error cancelling command on page leave:', error);
        }
      } else {
        console.log('No active command UUID to cancel on page leave');
      }

      // Clean up when user confirms leaving
      this.showingConnectionPrompt = false;
      this.jitInProgress = false;
      this.isPending = false;
      clearTimeout(this.fetchTimeout);
      window.removeEventListener('beforeunload', this.handleBeforeUnload);

      next();
    } catch {
      next(false);
    }
  },
};
</script>

<style lang="scss" scoped>
.heading {
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 10px;
}

.back-link {
  margin-bottom: 15px;
}

.section {
  text-align: center;
  margin-bottom: 8px;

  &__message {
    margin-bottom: 8px;
  }

  &__loader {
    margin-top: 16px;
  }

  &__button {
    margin-bottom: 16px;
  }
}

.jit-section {
  margin-top: 16px;
  padding: 12px;
  background-color: var(--secondary-bg);
  border-radius: 8px;

  &__notice {
    margin-bottom: 12px;
    line-height: 1.3;
  }

  &__success {
    color: var(--text-success);
    font-weight: 500;
    margin-bottom: 12px;
  }

  &__error {
    color: var(--text-error);
    font-size: 14px;
    margin-top: 8px;
    margin-bottom: 12px;
    font-style: italic;
  }

  &__button {
    margin-top: 8px;
  }
}

.commands {
  margin-top: 16px;
}

/* Connection Dialog Styles */
.connection-dialog {
  padding: 15px;
  max-height: 100%;
  color: var(--text-primary);

  &__content {
    margin-bottom: 15px;
    white-space: pre-line;

    p {
      margin: 0;
      line-height: 1.4;
    }
  }

  &__buttons {
    display: flex;
    justify-content: flex-end;
    border-top: 1px solid var(--tertiary-accent);
    padding-top: 15px;
    margin-bottom: 0;

    button {
      margin-right: 10px;
    }

    a.g-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      text-decoration: none;
    }
  }
}

/* Override modal styles for proper theming */
::v-deep .vm--modal[name="connection-dialog"] {
  background-color: var(--bg);
  border: 2px solid var(--lighter-primary);
}
</style>

<!-- Global styles for the modal -->
<style lang="scss">
/* Target the connection-dialog modal container - try multiple selectors */
.vm--modal[data-modal="connection-dialog"],
.vm--modal[name="connection-dialog"],
.vm--container .vm--modal {
  background-color: var(--bg) !important;
  border: 2px solid var(--lighter-primary) !important;
  color: var(--text-primary) !important;
}

/* Ensure overlay has proper opacity */
.vm--overlay {
  background: rgba(0, 0, 0, var(--modal-overlay-opacity)) !important;
}
</style>
